import styled from 'styled-components';

import { getUnitatBgColor, getUnitatColor } from '../../utils/unitats';

export const SchoolYearSelectorContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 20px;
`;

export const ControlsContainer = styled.div`
	display: flex;
	align-items: center; 
	justify-content: space-between; 
	gap: 32px; 
	margin-bottom: 32px; 
	width: 100%;
	flex-wrap: wrap;

	select {
		padding: 8px 16px;
		border: 1px solid #ddd;
		border-radius: 6px;
		font-size: 1em;
		background: white;
		cursor: pointer;
		transition: all 0.2s ease;

		&:hover {
			border-color: #3498db;
		}
	}

	${SchoolYearSelectorContainer} button {
		padding: 8px 16px;
		background: #3498db;
		color: white;
		border: none;
		border-radius: 6px;
		font-size: 1em;
		cursor: pointer;
		transition: all 0.2s ease;

		&:hover {
			background: #2980b9;
		}
	}

	@media (max-width: 768px) {
		flex-direction: column;
		align-items: stretch; 
		gap: 16px;
		margin-bottom: 24px;
	}
`;

interface UnitatToggleButtonProps {
	unitat: string;
	isActive: boolean;
}

export const UnitatToggleButton = styled.button<UnitatToggleButtonProps>`
	padding: 6px 12px;
	border-radius: 6px;
	font-size: 0.8em;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
	border: 2px solid ${({ unitat }) => getUnitatColor(unitat)};
	background: ${({ isActive, unitat }) =>
		isActive ? getUnitatColor(unitat) : 'transparent'};
	color: ${({ isActive, unitat }) => (isActive ? 'white' : getUnitatColor(unitat))};
	opacity: ${({ isActive }) => (isActive ? 1 : 0.5)};
`;

export const UnitatToggleContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
`;

export const FilterLabel = styled.span`
	font-size: 0.9em;
	font-weight: 500;
	color: #666;
	margin-right: 4px;
`;

interface AllToggleButtonProps {
	isAllActive: boolean;
}

export const AllToggleButton = styled.button<AllToggleButtonProps>`
	padding: 6px 12px;
	border-radius: 6px;
	font-size: 0.8em;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
	border: 2px solid #666;
	background: ${({ isAllActive }) => (isAllActive ? '#666' : 'transparent')};
	color: ${({ isAllActive }) => (isAllActive ? 'white' : '#666')};
	opacity: ${({ isAllActive }) => (isAllActive ? 1 : 0.6)};

	&:hover {
		opacity: 1;
	}
`;

export const MonthContainer = styled.div`
	width: 100%;
	box-sizing: border-box;
	margin-bottom: 60px;
	background: #f9fbfc;
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
`;

export const MonthTitle = styled.h2`
	color: #2c3e50;
	margin: 0 0 24px 0;
	font-size: 1.8em;
	border-bottom: 2px solid #e1e8ed;
	padding-bottom: 12px;
`;

export const EventsContainer = styled.div`
	column-width: 450px;
	column-gap: 16px;
`;

interface EventCardProps {
	unitat: string;
}

export const EventCard = styled.div<EventCardProps>`
	background: #fff;
	border-radius: 10px;
	padding: 0;
	box-shadow: 0 2px 8px rgba(52, 152, 219, 0.08);
	position: relative;
	display: grid;
	grid-template-columns: 90px 1fr auto;
	align-items: stretch;
	overflow: hidden;
	min-width: 100%;
	cursor: pointer;
	break-inside: avoid;
	margin-bottom: 16px;
	transition: transform 0.2s ease, box-shadow 0.2s ease;

	&:hover {
		transform: translateY(-2px);
		box-shadow: 0 4px 16px rgba(52, 152, 219, 0.15);
	}

	@media (max-width: 768px) {
		grid-template-columns: 75px 1fr;
	}
`;

export const UnitatSeparator = styled.div<{ unitat: string }>`
	width: 3px;
	background: ${({ unitat }) => getUnitatColor(unitat)};
	position: absolute;
	left: 90px;
	top: 0;
	bottom: 0;
	opacity: 0.7;

	@media (max-width: 768px) {
		left: 75px;
	}
`;

export const EventDate = styled.div<{ unitat: string }>`
	background: ${({ unitat }) => getUnitatBgColor(unitat)};
	color: #3c4043;
	padding: 16px 14px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	font-size: 0.85em;
	font-weight: 500;
	line-height: 1.3;
	width: 100%;
`;

export const EventContent = styled.div`
	flex: 1;
	padding: 16px;
	display: flex;
	flex-direction: column;
	min-height: 120px;
	overflow: hidden;
`;

export const TitleRow = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 8px;
	min-height: 48px;
`;

export const EventTitle = styled.h3`
	margin: 0;
	font-size: 1.1em;
	color: #2c3e50;
	line-height: 1.3;
	flex: 1;
	word-break: break-word;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	min-height: 28px;
`;

export const EventDescription = styled.p`
	margin: 0;
	font-size: 0.9em;
	color: #666;
	line-height: 1.4;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
`;

export const ActionButtons = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 12px;
	background: #f8f9fa;
	border-left: 1px solid #e9ecef;
	justify-content: center;
	flex-shrink: 0;

	@media (max-width: 768px) {
		flex-direction: row;
		padding: 8px;
	}
`;

const BaseActionButton = styled.button`
	padding: 5px 10px;
	border-radius: 5px;
	font-size: 0.8em;
	cursor: pointer;
	border: none;
	transition: all 0.2s ease;
	font-weight: 500;
	white-space: nowrap;
`;

export const EditButton = styled(BaseActionButton)`
	background: #f1f9ff;
	color: #3498db;

	&:hover {
		background: #3498db;
		color: white;
	}
`;

export const DeleteButton = styled(BaseActionButton)`
	background: #fff5f5;
	color: #e74c3c;

	&:hover {
		background: #e74c3c;
		color: white;
	}
`;

// Modal styles
export const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
	padding: 20px;
`;

export const ModalContent = styled.div`
	background: white;
	border-radius: 12px;
	max-width: 600px;
	width: 100%;
	max-height: 80vh;
	overflow-y: auto;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`;

export const ModalHeader = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	padding: 24px 24px 16px 24px;
	border-bottom: 1px solid #e9ecef;
`;

export const ModalTitle = styled.h2`
	margin: 0;
	font-size: 1.4em;
	color: #2c3e50;
	line-height: 1.3;
	flex: 1;
	padding-right: 16px;
`;

export const ModalClose = styled.button`
	background: none;
	border: none;
	font-size: 1.5em;
	cursor: pointer;
	color: #666;
	padding: 0;
	line-height: 1;
	transition: color 0.2s ease;

	&:hover {
		color: #333;
	}
`;

export const ModalBody = styled.div`
	padding: 24px;
`;

export const ModalUnitatLabel = styled.span<{ unitat: string }>`
	display: inline-block;
	background: ${({ unitat }) => getUnitatColor(unitat)};
	color: white;
	font-size: 0.7em;
	font-weight: 600;
	padding: 4px 10px;
	border-radius: 4px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 12px;
`;

export const ModalDate = styled.div`
	font-size: 0.95em;
	color: #666;
	margin-bottom: 16px;
	display: flex;
	align-items: center;
	gap: 8px;
`;

export const ModalDescription = styled.div`
	font-size: 1em;
	color: #333;
	line-height: 1.6;
	white-space: pre-wrap;
`;

export const ModalActions = styled.div`
	display: flex;
	gap: 12px;
	padding: 16px 24px 24px 24px;
	border-top: 1px solid #e9ecef;
`;

export const ModalActionButton = styled.button`
	padding: 10px 20px;
	border-radius: 6px;
	font-size: 0.9em;
	cursor: pointer;
	border: none;
	transition: all 0.2s ease;
	font-weight: 500;

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

export const ModalEditButton = styled(ModalActionButton)`
	background: #3498db;
	color: white;

	&:hover {
		background: #2980b9;
	}
`;

export const ModalDeleteButton = styled(ModalActionButton)`
	background: #e74c3c;
	color: white;

	&:hover {
		background: #c0392b;
	}
`;

export const CreateEventButton = styled.button`
	position: fixed;
	bottom: 32px;
	right: 32px;
	padding: 16px 24px;
	background: #3498db;
	color: white;
	border: none;
	border-radius: 30px;
	font-size: 1em;
	cursor: pointer;
	box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
	transition: all 0.2s ease;

	&:hover {
		background: #2980b9;
		transform: translateY(-2px);
		box-shadow: 0 6px 16px rgba(52, 152, 219, 0.4);
	}
`;

// Date section components
export const DateContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 5px;
`;

export const Day = styled.span`
	font-size: 1.5em;
	font-weight: 700;
	line-height: 1.2;
`;

export const DayRange = styled.span`
	font-size: 1.5em;
	font-weight: 700;
	line-height: 1.2;
	white-space: nowrap;
`;

export const Month = styled.span`
	font-size: 0.85em;
	font-weight: 500;
	line-height: 1.2;
`;

export const MonthSmall = styled.span`
	font-size: 0.75em;
	font-weight: 500;
	line-height: 1.2;
`;

export const Divider = styled.span`
	font-size: 0.9em;
	color: #adb5bd;
	margin: 2px 0;
`;

export const UnitatBadge = styled.div<{ unitat: string }>`
	background: ${({ unitat }) => getUnitatColor(unitat)};
	color: white;
	font-size: 0.7em;
	font-weight: 600;
	padding: 4px 8px;
	border-radius: 4px;
	margin-bottom: 8px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
`;

export const UnitatMiniLabel = styled.span<{ unitat: string }>`
	display: inline-block;
	background: ${({ unitat }) => getUnitatColor(unitat)};
	color: white;
	font-size: 0.65em;
	font-weight: 600;
	padding: 2px 6px;
	border-radius: 3px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	align-self: flex-start;
	white-space: nowrap;
`;